/**
 * GEKHA Homepage Sections — Shared JavaScript
 * Vanilla JS only. No jQuery.
 *
 * Guard pattern: every init function skips elements that already have
 * data-g-init set, so loading this script multiple times (one per section)
 * never double-binds listeners or re-initialises Swiper instances.
 */

(function () {
  'use strict';

  /* ── Tab switching (filterable grids §9 & §13) ──────────────── */
  function initFilterTabs() {
    document.querySelectorAll('.gekha-filter-grid:not([data-g-init])').forEach(function (grid) {
      grid.setAttribute('data-g-init', '1');

      var pills  = grid.querySelectorAll('.gekha-tab-pill');
      var panels = grid.querySelectorAll('.gekha-tab-panel');

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          var target = pill.dataset.tab;

          pills.forEach(function (p) {
            p.classList.remove('is-active');
            p.setAttribute('aria-selected', 'false');
          });
          panels.forEach(function (p) { p.classList.remove('is-active'); });

          pill.classList.add('is-active');
          pill.setAttribute('aria-selected', 'true');

          var panel = grid.querySelector('.gekha-tab-panel[data-tab="' + target + '"]');
          if (panel) panel.classList.add('is-active');
        });
      });

      // Activate first tab by default
      if (pills.length > 0 && panels.length > 0) {
        pills[0].classList.add('is-active');
        pills[0].setAttribute('aria-selected', 'true');
        panels[0].classList.add('is-active');
      }
    });
  }

  /* ── FAQ Accordion (§17) ────────────────────────────────────── */
  function initFaqAccordion() {
    document.querySelectorAll('.gekha-faq-item__trigger:not([data-g-init])').forEach(function (trigger) {
      trigger.setAttribute('data-g-init', '1');

      trigger.addEventListener('click', function () {
        var item    = trigger.closest('.gekha-faq-item');
        var isOpen  = item.classList.contains('is-open');
        var faqList = item.closest('.gekha-faq__list');

        // Close all siblings
        if (faqList) {
          faqList.querySelectorAll('.gekha-faq-item').forEach(function (i) {
            i.classList.remove('is-open');
            var t = i.querySelector('.gekha-faq-item__trigger');
            if (t) t.setAttribute('aria-expanded', 'false');
          });
        }

        // Open current (unless it was already open — click toggles closed)
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── UGC "Read more" toggle (§16) ───────────────────────────── */
  function initReadMore() {
    document.querySelectorAll('.gekha-ugc-card__read-more:not([data-g-init])').forEach(function (btn) {
      btn.setAttribute('data-g-init', '1');

      btn.addEventListener('click', function () {
        var review = btn.previousElementSibling;
        if (!review) return;
        var expanded = review.classList.toggle('is-expanded');
        btn.textContent = expanded ? 'Read less' : 'Read more';
      });
    });
  }

  /* ── Wishlist: localStorage-persisted, shared by every button on the
     site ([data-product-id] + .pc-wishlist / .gekha-pdp-wishlist). Stores
     {id, handle} pairs so the wishlist page can look products back up. ── */
  var WISHLIST_KEY = 'gekha_wishlist';

  function getWishlist() {
    try {
      var raw = JSON.parse(localStorage.getItem(WISHLIST_KEY));
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function isWished(id) {
    id = String(id);
    return getWishlist().some(function (item) { return item.id === id; });
  }

  function toggleWishlistId(id, handle) {
    id = String(id);
    var list = getWishlist();
    var idx = list.findIndex(function (item) { return item.id === id; });
    var wished;
    if (idx === -1) {
      list.push({ id: id, handle: handle || '' });
      wished = true;
    } else {
      list.splice(idx, 1);
      wished = false;
    }
    saveWishlist(list);
    document.dispatchEvent(new CustomEvent('gekha:wishlist:changed', {
      detail: { productId: id, wished: wished, count: list.length }
    }));
    return wished;
  }

  /* Re-sync every wishlist button on the page (used after any toggle, in
     case the same product's button appears more than once). */
  function syncWishlistButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var wished = isWished(btn.dataset.productId);
      btn.classList.toggle('is-wished', wished);
      var name = btn.dataset.productName || 'item';
      btn.setAttribute('aria-label', (wished ? 'Remove ' : 'Add ') + name + (wished ? ' from wishlist' : ' to wishlist'));
    });
  }

  /* Header icon badge — how many products are saved. */
  function syncWishlistCount() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
    });
  }

  function initWishlist() {
    document.querySelectorAll('.pc-wishlist:not([data-g-init]), .gekha-pdp-wishlist:not([data-g-init])').forEach(function (btn) {
      btn.setAttribute('data-g-init', '1');
      btn.setAttribute('data-wishlist-btn', '1');

      if (isWished(btn.dataset.productId)) btn.classList.add('is-wished');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        toggleWishlistId(btn.dataset.productId, btn.dataset.productHandle);
        syncWishlistButtons();
      });
    });
  }

  document.addEventListener('gekha:wishlist:changed', syncWishlistButtons);
  document.addEventListener('gekha:wishlist:changed', syncWishlistCount);

  /* Some pages (e.g. collection filtering, related products) replace their
     product grid via their own AJAX/section-render flow after the normal
     init() timing, with no shared event to hook into — a MutationObserver
     catches new wishlist buttons regardless of what triggered them.
     initWishlist() is cheap and self-guarded, safe to call repeatedly. */
  if ('MutationObserver' in window && !document.documentElement.dataset.gWishlistObserver) {
    document.documentElement.dataset.gWishlistObserver = '1';
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) { initWishlist(); break; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── Shared toast — wishlist add/remove, share-link-copied, etc. ── */
  var toastTimer = null;
  function showToast(message) {
    var toast = document.getElementById('gekha-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'gekha-toast';
      toast.className = 'gekha-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2000);
  }
  document.addEventListener('gekha:wishlist:changed', function (e) {
    showToast(e.detail.wished ? 'Added to Wishlist' : 'Removed from Wishlist');
  });

  /* ── Bottom nav height sync (keeps body padding matched to real height) ── */
  function initBottomNavHeightVar() {
    var nav = document.querySelector('.gekha-bottom-nav:not([data-g-init])');
    if (!nav) return;
    nav.setAttribute('data-g-init', '1');

    function setVar() {
      document.documentElement.style.setProperty('--gekha-bottom-nav-height', nav.offsetHeight + 'px');
    }
    setVar();
    window.addEventListener('resize', setVar);
  }

  /* ── Hero Carousel init ─────────────────────────────────────── */
  function initHeroCarousels() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.gekha-hero-swiper:not([data-g-init])').forEach(function (el) {
      el.setAttribute('data-g-init', '1');
      var autoplay = el.dataset.autoplay === 'true';
      var speed    = parseInt(el.dataset.speed, 10) || 5000;
      new Swiper(el, {
        loop: true,
        speed: 600,
        autoplay: autoplay ? { delay: speed, disableOnInteraction: false } : false,
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true
        },
        a11y: { enabled: true }
      });
    });
  }

  /* ── Influencer Carousel init ───────────────────────────────── */
  function initInfluencerCarousels() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.gekha-influencer-swiper:not([data-g-init])').forEach(function (el) {
      el.setAttribute('data-g-init', '1');
      var wrap = el.closest('.gekha-influencer');
      new Swiper(el, {
        loop: false,
        slidesPerView: 1.4,
        spaceBetween: 10,
        freeMode: true,
        navigation: {
          nextEl: wrap ? wrap.querySelector('.swiper-button-next') : null,
          prevEl: wrap ? wrap.querySelector('.swiper-button-prev') : null
        },
        breakpoints: {
          480:  { slidesPerView: 2.2 },
          750:  { slidesPerView: 3.2, spaceBetween: 14 },
          1100: { slidesPerView: 4.2, spaceBetween: 16 }
        }
      });
    });
  }

  /* ── PDP: guard against "ghost click" auto-opening the zoom modal ──
     Symptom: tapping a product card on a collection page can deliver a
     delayed synthetic click to the PDP's main image link if it lands at the
     same screen coordinates, auto-opening the gallery-viewer zoom modal
     right on arrival. A real tap always has a pointerdown/touchstart on the
     same element immediately before the click; a ghost click doesn't. */
  function initGalleryGhostClickGuard() {
    if (document.documentElement.dataset.gGalleryGuard) return;
    document.documentElement.dataset.gGalleryGuard = '1';

    var navigatedAt = Date.now();
    var sawRealPress = false;

    function markRealPress(evt) {
      if (evt.target.closest && evt.target.closest('.show-gallery')) sawRealPress = true;
    }
    document.addEventListener('pointerdown', markRealPress, true);
    document.addEventListener('touchstart', markRealPress, { capture: true, passive: true });

    document.addEventListener('click', function (evt) {
      var link = evt.target.closest && evt.target.closest('.show-gallery');
      if (!link) return;
      if (!sawRealPress && Date.now() - navigatedAt < 500) {
        evt.stopImmediatePropagation();
        evt.preventDefault();
      }
    }, true);
  }

  /* ── PDP: gallery overlay badges ────────────────────────────── */
  function initPdpOverlays() {
    var dataEl = document.getElementById('gekha-pdp-data');
    if (!dataEl || dataEl.dataset.gInit) return;
    dataEl.dataset.gInit = '1';

    var mainImage = document.querySelector('.media-gallery .main-image');
    if (!mainImage) return;

    var ribbon  = dataEl.dataset.ribbon;
    var rating  = dataEl.dataset.rating  ? parseFloat(dataEl.dataset.rating) : 0;
    var count   = dataEl.dataset.ratingCount || '';
    var type    = dataEl.dataset.type || '';
    var discount = dataEl.dataset.discount ? parseInt(dataEl.dataset.discount, 10) : 0;
    var savings  = dataEl.dataset.savings ? parseInt(dataEl.dataset.savings, 10) : 0;
    var showTax  = dataEl.dataset.showTax === 'true';
    var taxText  = dataEl.dataset.taxText || 'Inclusive of all taxes';

    /* Build overlay wrapper */
    var root = document.createElement('div');
    root.className = 'gekha-pdp-overlay-root';
    root.setAttribute('aria-hidden', 'true');

    if (ribbon) {
      var rib = document.createElement('span');
      rib.className = 'gekha-pdp-ribbon';
      rib.textContent = ribbon;
      root.appendChild(rib);
    }

    if (rating > 0) {
      var ratingEl = document.createElement('div');
      ratingEl.className = 'gekha-pdp-rating-badge';
      ratingEl.innerHTML =
        '<svg width="11" height="11" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" fill="#bf570a">' +
        '<path d="M5 .5 6.18 3.26l3 .44-2.17 2.11.51 2.99L5 7.38 2.48 8.8l.51-3L.82 3.7l3-.44z"/>' +
        '</svg>' +
        '<span>' + rating.toFixed(1) + '</span>' +
        (count ? '<span class="gekha-pdp-rating-count">(' + count + ')</span>' : '');
      root.appendChild(ratingEl);
    }

    if (type) {
      var pill = document.createElement('span');
      pill.className = 'gekha-pdp-category-pill';
      pill.textContent = type.toUpperCase();
      root.appendChild(pill);
    }

    mainImage.appendChild(root);

    /* Discount badge — inject next to price */
    if (discount > 0) {
      var priceRow = document.querySelector('.product-price--large');
      if (priceRow) {
        var badge = document.createElement('span');
        badge.className = 'gekha-pdp-discount-badge';
        badge.textContent = discount + '% off';
        priceRow.appendChild(badge);
      }
    }

    /* Wishlist icon — inject into the price row, pushed to the right
       (that row is a flex container, margin-left:auto on the last item
       floats it to the end). Reuses the shared wishlist system, so it
       stays in sync with the product-card hearts everywhere else. */
    var productId = dataEl.dataset.productId;
    if (productId) {
      var priceRowForWishlist = document.querySelector('.product-price--large');
      if (priceRowForWishlist) {
        var wishBtn = document.createElement('button');
        wishBtn.type = 'button';
        wishBtn.className = 'gekha-pdp-wishlist';
        wishBtn.setAttribute('data-product-id', productId);
        wishBtn.setAttribute('data-product-handle', dataEl.dataset.productHandle || '');
        wishBtn.setAttribute('data-product-name', dataEl.dataset.productTitle || 'item');
        wishBtn.setAttribute('aria-label', 'Add ' + (dataEl.dataset.productTitle || 'item') + ' to wishlist');
        wishBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke-linejoin="round"/>' +
          '</svg>';
        priceRowForWishlist.appendChild(wishBtn);
        initWishlist();

        /* Share icon — sits right next to the wishlist heart. Uses the
           native share sheet where available (mobile browsers), falls
           back to copying the product link on desktop. */
        var shareBtn = document.createElement('button');
        shareBtn.type = 'button';
        shareBtn.className = 'gekha-pdp-share';
        shareBtn.setAttribute('aria-label', 'Share ' + (dataEl.dataset.productTitle || 'this product'));
        shareBtn.innerHTML = '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>' +
          '<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>' +
          '</svg>';
        shareBtn.addEventListener('click', function () {
          var shareData = { title: dataEl.dataset.productTitle || document.title, url: window.location.href };
          if (navigator.share) {
            navigator.share(shareData).catch(function () {});
            return;
          }
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(shareData.url).then(function () {
              showToast('Link copied to clipboard');
            }).catch(function () {
              showToast('Could not copy link');
            });
            return;
          }
          showToast('Could not copy link');
        });
        priceRowForWishlist.appendChild(shareBtn);
      }
    }

    /* Savings note — inject below price row, above the tax note */
    if (savings > 0) {
      var savingsPriceContainer = document.querySelector('.price-container');
      if (savingsPriceContainer) {
        var savingsNote = document.createElement('span');
        savingsNote.className = 'gekha-pdp-savings';
        savingsNote.textContent = 'You save ' + formatMoneyINR(savings);
        savingsPriceContainer.appendChild(savingsNote);
      }
    }

    /* Tax note — inject below price container */
    if (showTax) {
      var priceContainer = document.querySelector('.price-container');
      if (priceContainer) {
        var note = document.createElement('span');
        note.className = 'gekha-pdp-tax-note';
        note.textContent = taxText;
        priceContainer.appendChild(note);
      }
    }
  }

  /* ── PDP: sticky bar — size display, variant sync, Buy Now ──── */
  function initPdpStickyBar() {
    var bar = document.getElementById('gekha-pdp-sticky-bar');
    if (!bar || bar.dataset.gInit) return;
    bar.dataset.gInit = '1';

    var stickyInput   = document.getElementById('gekha-sticky-variant-id');
    var buyNowBtn     = document.getElementById('gekha-buy-now-btn');
    var atcBtn        = document.getElementById('gekha-atc-btn');
    var sizeRow       = document.getElementById('gekha-sticky-size-row');
    var sizeValueEl   = document.getElementById('gekha-sticky-size-value');

    /* ── Build a variant lookup map from the page's embedded JSON ─
       Symmetry 8 embeds variants JSON inside <variant-picker> as a
       <script type="application/json"> block, or as a full product JSON.
       We try multiple locations so we always have availability data.       */
    var variantMap = {};

    function buildVariantMap(variants) {
      if (!Array.isArray(variants)) return;
      variants.forEach(function (v) { variantMap[String(v.id)] = v; });
    }

    /* Try 1: variants array inside <variant-picker> */
    var vpJsonEl = document.querySelector('variant-picker script[type="application/json"]');
    if (vpJsonEl) {
      try { buildVariantMap(JSON.parse(vpJsonEl.textContent)); } catch (e) {}
    }

    /* Try 2: full product JSON embedded anywhere on the page */
    if (!Object.keys(variantMap).length) {
      document.querySelectorAll('script[type="application/json"]').forEach(function (el) {
        if (Object.keys(variantMap).length) return;
        try {
          var data = JSON.parse(el.textContent);
          if (data && data.variants) buildVariantMap(data.variants);
          else if (Array.isArray(data) && data[0] && data[0].id && data[0].options) buildVariantMap(data);
        } catch (e) {}
      });
    }

    /* ── Find the main product form and its hidden variant input ── */
    var mainForm  = document.querySelector('form.js-product-form') ||
                    document.querySelector('.js-product-form[data-product-id]');
    var mainInput = mainForm ? mainForm.querySelector('input[name="id"]') : null;

    /* ── Scroll to the variant picker in the main form ──────────── */
    function scrollToSizePicker() {
      var picker = document.querySelector('variant-picker') ||
                   document.querySelector('.option-selector') ||
                   document.querySelector('#product-info');
      if (picker) {
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var optBtns = document.querySelector('.option-selector__btns');
        if (optBtns) {
          optBtns.style.transition = 'box-shadow 0.2s';
          optBtns.style.boxShadow = '0 0 0 2px #bf570a';
          setTimeout(function () { optBtns.style.boxShadow = ''; }, 1200);
        }
      }

      var sizeError = document.getElementById('gekha-size-error');
      if (sizeError) sizeError.hidden = false;
    }

    /* Hide the "Select a size" message once a size has been chosen */
    function hideSizeError() {
      var sizeError = document.getElementById('gekha-size-error');
      if (sizeError) sizeError.hidden = true;
    }

    /* Tapping the size row scrolls to the picker */
    if (sizeRow) {
      sizeRow.addEventListener('click', scrollToSizePicker);
      sizeRow.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') scrollToSizePicker();
      });
    }

    /* ── Apply a variant object to the sticky bar ─────────────── */
    function applyVariant(variant) {
      if (!variant) return;

      /* Update hidden form input */
      if (stickyInput) stickyInput.value = variant.id;
      hideSizeError();

      /* Show variant title in size row */
      if (sizeValueEl) {
        var label = variant.title && variant.title !== 'Default Title'
          ? variant.title
          : 'Selected';
        sizeValueEl.textContent = label;
        if (sizeRow) sizeRow.classList.remove('gekha-sticky-size-row--unselected');
      }

      /* Enable/disable buttons based on availability.
         buyNowBtn now wraps Shopify's native dynamic checkout button
         (rendered via {{ form | payment_button }}), which manages its own
         enabled state from the form's inputs — we just dim the wrapper. */
      var avail = variant.available;
      if (buyNowBtn) {
        buyNowBtn.classList.toggle('is-disabled', !avail);
      }
      if (atcBtn) {
        atcBtn.classList.toggle('is-disabled', !avail);
        atcBtn.setAttribute('aria-disabled', String(!avail));
        atcBtn.textContent = avail ? atcBtn.getAttribute('aria-label') : 'Sold Out';
      }
    }

    /* ── Guard taps while no size is selected ─────────────────────
       Both buttons stay tappable (not a real disabled state) so a tap
       while disabled scrolls to the size picker instead of doing
       nothing. The sticky form's submit is a real native submission
       (no AJAX handler here), so gating "submit" covers Add to Cart
       from both click and keyboard; Buy Now's payment button doesn't
       reliably go through the form's submit event, so it gets its own
       guard overlay (gekha-sticky-btn-guard, see CSS) that only
       captures taps while its wrapper is .is-disabled. */
    var stickyForm = document.getElementById('gekha-sticky-form');
    if (stickyForm) {
      stickyForm.addEventListener('submit', function (e) {
        if (!stickyInput || !stickyInput.value) {
          e.preventDefault();
          scrollToSizePicker();
        }
      });
    }

    var buyNowGuard = document.getElementById('gekha-buy-now-guard');
    if (buyNowGuard) {
      buyNowGuard.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        scrollToSizePicker();
      });
    }

    /* ── Sync from main form's input[name="id"] ───────────────────
       NOTE: Shopify/Symmetry theme JS sets input.value as a property
       (not an HTML attribute), so MutationObserver on attributes does
       NOT fire. Instead we read the value after a microtask delay.   */
    function syncFromMainForm() {
      if (!mainInput) return;
      var vid = String(mainInput.value);
      if (!vid) return;

      /* Sync sticky hidden input */
      if (stickyInput) stickyInput.value = vid;

      /* Use our variant map for full data (title + availability) */
      var variant = variantMap[vid];
      if (variant) {
        applyVariant(variant);
      } else {
        /* Variant map not populated — at minimum sync the ID.
           Fetch availability from Shopify variants API as fallback. */
        fetch('/variants/' + vid + '.js')
          .then(function (r) { return r.json(); })
          .then(function (v) { variantMap[vid] = v; applyVariant(v); })
          .catch(function () {
            /* Last resort: just update the variant ID, keep buttons enabled */
            if (stickyInput) stickyInput.value = vid;
          });
      }
    }

    /* ── Event sources that indicate a variant change ─────────────
       We listen to ALL of these because different Shopify theme versions
       fire different events, and we want zero misses.                */

    /* Source 1: Symmetry 8 / OS2 themes dispatch 'variant:change' */
    document.addEventListener('variant:change', function (e) {
      if (e.detail && e.detail.variant) applyVariant(e.detail.variant);
    });

    /* Source 2: Some themes dispatch 'theme:variant:update' */
    document.addEventListener('theme:variant:update', function (e) {
      if (e.detail && e.detail.variant) applyVariant(e.detail.variant);
    });

    /* Source 3 (PRIMARY FIX): change events on option radio inputs.
       Fires whenever any option (color OR size) is clicked.
       We use requestAnimationFrame so Symmetry's own JS runs first
       and updates input[name="id"] before we read it.               */
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el) return;
      var inVariantPicker = el.closest('variant-picker') ||
                            el.closest('.option-selector') ||
                            (el.classList && el.classList.contains('opt-btn'));
      if (!inVariantPicker) return;

      /* One rAF is usually enough; add a 0ms timeout as belt-and-suspenders */
      requestAnimationFrame(function () {
        setTimeout(syncFromMainForm, 0);
      });
    });

    /* Source 4: URL changes — Symmetry updates ?variant=ID in the URL */
    window.addEventListener('popstate', function () {
      requestAnimationFrame(syncFromMainForm);
    });

    /* Mark size row as "unselected" if no real variant chosen yet */
    if (sizeRow && sizeValueEl && sizeValueEl.textContent.trim() === 'Select size') {
      sizeRow.classList.add('gekha-sticky-size-row--unselected');
    }

    /* Auto-hide sticky bar when the inline buy button is in viewport */
    var inlineAtc = document.querySelector('.buy-buttons-row');
    if (inlineAtc && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-hidden', entry.isIntersecting);
        });
      }, { threshold: 0.5 }).observe(inlineAtc);
    }
  }

  /* ── PDP: Fabric Features slider ──────────────────────────────
     Lives inline in the product info column now (not a full-width
     section), so above 750px it sits in the narrow ~38% side column
     rather than spanning the page — fewer slides per view than before. */
  function initFabricFeaturesSliders() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.gekha-fabric-swiper:not([data-g-init])').forEach(function (el) {
      el.setAttribute('data-g-init', '1');
      new Swiper(el, {
        loop: false,
        slidesPerView: 2,
        spaceBetween: 12,
        breakpoints: {
          750: {
            slidesPerView: 1.6,
            spaceBetween: 14
          }
        },
        pagination: {
          el: el.querySelector('.gekha-fabric-pagination'),
          clickable: true
        },
        a11y: { enabled: true }
      });
    });
  }

  /* ── Shared: horizontal scroller with arrow nav + autoplay ───────
     Prev/next arrow buttons (if present) scroll the row via click, each
     disabling itself at its corresponding end. Native touch swipe /
     trackpad horizontal scroll on the row itself is unaffected. Also
     auto-advances on a timer, looping back to the start at the end;
     paused while the user is interacting (hover, touch, or the arrow
     buttons) and resumes a few seconds after they stop. Used by the PDP
     customer-stories row and the related-products slider. */
  function setupHorizontalScroller(grid, navWrapSelector, prevSelector, nextSelector, itemSelector) {
    var navWrap = grid.closest(navWrapSelector);
    if (!navWrap) return;

    var prevBtn = navWrap.querySelector(prevSelector);
    var nextBtn = navWrap.querySelector(nextSelector);
    if (!prevBtn || !nextBtn) return;

    var gap = parseFloat(getComputedStyle(grid).columnGap) || 12;

    var scrollByAmount = function () {
      var item = grid.querySelector(itemSelector);
      return item ? item.getBoundingClientRect().width + gap : grid.clientWidth * 0.9;
    };

    var updateButtons = function () {
      var atStart = grid.scrollLeft <= 0;
      var atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    };

    prevBtn.addEventListener('click', function () {
      grid.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
      pauseThenResumeAutoScroll();
    });
    nextBtn.addEventListener('click', function () {
      grid.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
      pauseThenResumeAutoScroll();
    });
    grid.addEventListener('scroll', updateButtons, { passive: true });
    updateButtons();

    var AUTO_SCROLL_INTERVAL = 3500;
    var AUTO_SCROLL_RESUME_DELAY = 5000;
    var autoScrollTimer = null;
    var resumeTimer = null;
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function stopAutoScroll() {
      if (autoScrollTimer) clearInterval(autoScrollTimer);
      autoScrollTimer = null;
    }

    function startAutoScroll() {
      stopAutoScroll();
      autoScrollTimer = setInterval(function () {
        var atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
        if (atEnd) {
          grid.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          grid.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
        }
      }, AUTO_SCROLL_INTERVAL);
    }

    function pauseThenResumeAutoScroll() {
      stopAutoScroll();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(startAutoScroll, AUTO_SCROLL_RESUME_DELAY);
    }

    if (!reducedMotion) {
      grid.addEventListener('mouseenter', stopAutoScroll);
      grid.addEventListener('mouseleave', startAutoScroll);
      grid.addEventListener('pointerdown', pauseThenResumeAutoScroll);
      startAutoScroll();
    }
  }

  /* ── PDP: Customer Stories — scroll + arrow nav ─────────────────── */
  function initCustomerStoriesScroll() {
    document.querySelectorAll('.pdp-customer-stories__grid:not([data-g-init])').forEach(function (grid) {
      grid.setAttribute('data-g-init', '1');
      setupHorizontalScroller(grid, '.pdp-customer-stories__nav-wrap', '.pdp-cs-nav--prev', '.pdp-cs-nav--next', '.pdp-cs-card');
    });
  }

  /* ── PDP: Related products ("You may also like") slider ────────── */
  function initRelatedProductsSlider() {
    document.querySelectorAll('.gekha-related-products__grid:not([data-g-init])').forEach(function (grid) {
      grid.setAttribute('data-g-init', '1');
      setupHorizontalScroller(grid, '.gekha-related-products__nav-wrap', '.gekha-related-products__nav--prev', '.gekha-related-products__nav--next', '.pc-card');
    });
  }

  /* ── PDP: Hidden Gems size filter ───────────────────────────── */
  function initHiddenGems() {
    document.querySelectorAll('.gekha-hidden-gems:not([data-g-init])').forEach(function (section) {
      section.setAttribute('data-g-init', '1');

      var pills = section.querySelectorAll('.gekha-hidden-gems__size-pill');
      var cards = section.querySelectorAll('.gekha-hidden-gems__card-wrap');

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          var size = pill.dataset.size;

          pills.forEach(function (p) { p.classList.remove('is-active'); });
          pill.classList.add('is-active');

          cards.forEach(function (card) {
            if (size === 'all') {
              card.classList.remove('is-hidden');
            } else {
              var sizes = (card.dataset.sizes || '').toLowerCase().split(',');
              card.classList.toggle('is-hidden', !sizes.includes(size.toLowerCase()));
            }
          });
        });
      });
    });
  }

  /* ── Wishlist page: fetch + render saved products ─────────────
     No wishlist app is installed, so there's nothing server-side to
     query — the list of {id, handle} pairs lives in localStorage
     (see the wishlist system above), and this fetches each product's
     live data client-side to build cards matching the .pc-card look. */
  function formatMoneyINR(cents) {
    return 'Rs. ' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function initWishlistPage() {
    var grid = document.getElementById('gekha-wishlist-grid');
    var empty = document.getElementById('gekha-wishlist-empty');
    if (!grid || grid.dataset.gInit) return;
    grid.dataset.gInit = '1';

    var list = getWishlist();
    if (!list.length) {
      if (empty) empty.hidden = false;
      return;
    }

    Promise.all(list.map(function (item) {
      if (!item.handle) return null;
      return fetch('/products/' + item.handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var found = products.filter(Boolean);

      if (!found.length) {
        if (empty) empty.hidden = false;
        return;
      }

      found.forEach(function (p) {
        var hasDiscount = p.compare_at_price && p.compare_at_price > p.price;
        var discountPct = hasDiscount ? Math.round((p.compare_at_price - p.price) * 100 / p.compare_at_price) : 0;

        var card = document.createElement('div');
        card.className = 'pc-card';
        card.innerHTML =
          '<div class="pc-image-wrap">' +
            '<a class="pc-image-link" href="/products/' + p.handle + '" aria-label="' + p.title.replace(/"/g, '&quot;') + '">' +
              '<img class="pc-image" src="' + (p.featured_image || '') + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy">' +
            '</a>' +
            '<button type="button" class="pc-wishlist is-wished" data-product-id="' + p.id + '" data-product-handle="' + p.handle + '" data-product-name="' + p.title.replace(/"/g, '&quot;') + '" aria-label="Remove ' + p.title.replace(/"/g, '&quot;') + ' from wishlist">' +
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke-linejoin="round"/></svg>' +
            '</button>' +
          '</div>' +
          '<div class="pc-info">' +
            '<h3 class="pc-title"><a href="/products/' + p.handle + '">' + p.title + '</a></h3>' +
            '<div class="pc-price-row">' +
              '<span class="pc-price">' + formatMoneyINR(p.price) + '</span>' +
              (hasDiscount ? '<span class="pc-compare-price">' + formatMoneyINR(p.compare_at_price) + '</span><span class="pc-discount">' + discountPct + '% off</span>' : '') +
            '</div>' +
          '</div>';
        grid.appendChild(card);
      });

      grid.hidden = false;
      if (empty) empty.hidden = true;
      initWishlist();

      /* Clicking a card's heart here removes it — pull that card straight
         out of the DOM instead of re-fetching every remaining product,
         so the removal is instant instead of waiting on a network round
         trip for the whole grid. */
      document.addEventListener('gekha:wishlist:changed', function (e) {
        if (e.detail.wished) return;
        var btn = grid.querySelector('[data-product-id="' + e.detail.productId + '"]');
        if (!btn) return;
        var removedCard = btn.closest('.pc-card');
        if (removedCard) removedCard.remove();
        if (!grid.children.length) {
          grid.hidden = true;
          if (empty) empty.hidden = false;
        }
      });
    });
  }

  /* ── Collection: Libas-style filter bar ───────────────────── */
  function initCollectionFilterBar() {
    if (!document.body.classList.contains('template-collection')) return;

    var SORT_ICON = '<svg width="13" height="12" viewBox="0 0 12 11" fill="none" aria-hidden="true"><path d="M3.25 10L3.25 1M3.25 1L5.5 3.25M3.25 1L1 3.25M8.875 1L8.875 10M8.875 10L11.125 7.75M8.875 10L6.625 7.75" stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    function injectIntoBar(bar) {
      if (!bar) return;

      // Replace sort button content with a "Sort" label
      var sortBtn = bar.querySelector('.link-dropdown__button');
      if (sortBtn && !sortBtn.querySelector('.gekha-sort-label')) {
        var label = document.createElement('span');
        label.className = 'gekha-sort-label';
        label.innerHTML = SORT_ICON + '<span>Sort</span>';
        sortBtn.insertBefore(label, sortBtn.firstChild);
      }
    }

    // This bar is mobile-only (see gekha-sections.css); don't inject the
    // mobile-specific "Sort" label on desktop.
    if (!window.matchMedia('(max-width: 767.98px)').matches) return;

    // Inject into the main (non-sticky) utility bar
    var mainBar = document.querySelector('.utility-bar:not(.utility-bar--sticky-mobile-copy)');
    injectIntoBar(mainBar);

    // Symmetry creates the sticky copy via JS shortly after page load
    setTimeout(function () {
      var stickyBar = document.querySelector('.utility-bar--sticky-mobile-copy');
      injectIntoBar(stickyBar);
    }, 600);
  }


  /* ── PDP: pin the details, then hold the gallery ────────────────
     See "PDP desktop, two phases" in gekha-sections.css. Both columns are
     sticky; this script supplies the two numbers CSS can't know:
       --pdp-gallery-top  negative top (screen height - gallery height) so the
                          gallery scrolls until its bottom meets the bottom of
                          the screen, then holds. If the gallery is shorter than
                          the screen it just pins under the header.
       --pdp-row-min      the row must be taller than the details column by the
                          distance the gallery scrolls while the details are
                          pinned, otherwise a sticky column has no room to pin. */
  function initPdpStickyGallery() {
    if (!document.body.classList.contains('template-product')) return;
    var row = document.querySelector('.product-info');
    var gallery = row && row.querySelector('.product-column-left');
    var info = row && row.querySelector('.product-column-right');
    if (!gallery || !info || row.getAttribute('data-g-sticky')) return;
    row.setAttribute('data-g-sticky', '1');

    var mq = window.matchMedia('(min-width: 750px)');
    function update() {
      if (!mq.matches) {
        gallery.style.removeProperty('--pdp-gallery-top');
        row.style.removeProperty('--pdp-row-min');
        return;
      }
      var header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height')) || 0;
      var visible = Math.max(0, window.innerHeight - header - 32);
      var g = gallery.offsetHeight;
      var i = info.offsetHeight;
      gallery.style.setProperty('--pdp-gallery-top', Math.min(header + 16, window.innerHeight - g - 16) + 'px');
      var travel = Math.max(0, g - Math.min(i, visible));
      row.style.setProperty('--pdp-row-min', (i + travel) + 'px');
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(update);
      ro.observe(gallery);
      ro.observe(info);
    }
  }

  /* ── Init all ───────────────────────────────────────────────── */
  function init() {
    initFilterTabs();
    initFaqAccordion();
    initReadMore();
    initWishlist();
    syncWishlistCount();
    initBottomNavHeightVar();
    initHeroCarousels();
    initInfluencerCarousels();
    initGalleryGhostClickGuard();
    initPdpOverlays();
    initPdpStickyBar();
    initCustomerStoriesScroll();
    initRelatedProductsSlider();
    initHiddenGems();
    initFabricFeaturesSliders();
    initCollectionFilterBar();
    initPdpStickyGallery();
    initWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify theme-editor section events.
  // The data-g-init guards ensure only newly inserted elements are touched.
  document.addEventListener('shopify:section:load', init);

  // Related products are fetched via AJAX after initial page load (see
  // gekha-related-products.liquid), so the slider markup doesn't exist yet
  // when the listener above runs at DOMContentLoaded.
  document.addEventListener('gekha:related-products:loaded', function () {
    initRelatedProductsSlider();
    initWishlist();
  });
})();
